import { useEffect, useMemo, useState } from 'react';
import { useAtom, atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { analyticsV2Service } from '@/services/analyticsV2.service';
import { queryClient } from '@/lib/query-client';
import type {
  AnalyticsV2SeriesQueryParams,
  CategoryTableRow,
  CoverageFunnelResult,
  Granularity,
  ScalarMetricKey,
  ScalarMetricResult,
  SeriesMetricKey,
  SeriesMetricsBatch,
  TicketCategoryBucket,
} from '@/types/analyticsV2';

/**
 * Per-key scalar metric fetcher — each key resolves independently through the
 * shared queryClient cache. Built on `queryClient.fetchQuery` rather than
 * `useQueries` because only `@tanstack/query-core` is installed (no React
 * adapter).
 */
export interface PerKeyMetricResult {
  key: ScalarMetricKey;
  data: ScalarMetricResult | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Per-category rollup fetcher for the Ticket Metrics tab. Returns one entry
 * per category with the metrics the categories table renders. Same caching
 * pattern as `useSingleMetric` so re-rendering the tab does not refetch.
 */
export interface UseCategoriesResult {
  data: CategoryTableRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The brand belongs in the cache KEY, not only in the request. Switching brands does not
 * clear the cache, so without it the second brand is served the first brand's numbers.
 * `'all'` rather than the bare null so an unscoped read cannot collide with a brand id.
 */
export function useCategories(
  fromDate: string,
  toDate: string,
  accountId: number,
  ticketCategory?: TicketCategoryBucket,
  // Last on purpose: `TicketCategoryBucket` is a string union and would be assignable to a
  // `string` parameter, so a caller passing the category positionally into a `buId` slot
  // ahead of it would type-check and silently scope the request to a brand named "CS".
  buId?: string | null,
): UseCategoriesResult {
  const [state, setState] = useState<UseCategoriesResult>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'categories', accountId, fromDate, toDate, ticketCategory ?? null, buId ?? 'all'],
        queryFn: () =>
          analyticsV2Service.getCategories({ fromDate, toDate, ticketCategory, accountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((rows) => {
        if (cancelled) return;
        setState({ data: rows, isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, ticketCategory, accountId, buId]);

  return state;
}

/**
 * Coverage Funnel fetcher for the super-admin Internal Analytics page. Same
 * caching pattern as `useCategories` — one query keyed by account/from/to,
 * disabled until both dates are present, so re-rendering the tab does not
 * refetch.
 */
export interface UseFunnelResult {
  data: CoverageFunnelResult | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useFunnel(
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): UseFunnelResult {
  const [state, setState] = useState<UseFunnelResult>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'funnel', accountId, fromDate, toDate, buId ?? 'all'],
        queryFn: () =>
          analyticsV2Service.getFunnel({ fromDate, toDate, accountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((result) => {
        if (cancelled) return;
        setState({ data: result, isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, accountId, buId]);

  return state;
}

const METRIC_STALE_TIME = 60 * 1000;

function useSingleMetric(
  key: ScalarMetricKey,
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): PerKeyMetricResult {
  const [state, setState] = useState<Omit<PerKeyMetricResult, 'key'>>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'metric', accountId, fromDate, toDate, key, buId ?? 'all'],
        queryFn: () =>
          analyticsV2Service.getMetrics([key], { fromDate, toDate, accountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((batch) => {
        if (cancelled) return;
        setState({ data: batch[key], isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [key, fromDate, toDate, accountId, buId]);

  return { key, ...state };
}

/**
 * Fan out one request per key. Callers pass a stable `keys` array (typically
 * the module-level `SCALAR_METRIC_KEYS` constant) — the hook calls
 * `useSingleMetric` once per index. The array length must be stable across
 * renders (it is — `SCALAR_METRIC_KEYS` is a frozen const).
 */
export function useMetricsPerKey(
  keys: ScalarMetricKey[],
  fromDate: string,
  toDate: string,
  accountId: number,
  buId?: string | null,
): PerKeyMetricResult[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return keys.map((key) => useSingleMetric(key, fromDate, toDate, accountId, buId));
}


// ─── Series ───────────────────────────────────────────────────────────────────
// Atom-driven fetcher that refetches whenever params change. Series is fetched
// separately from scalar because it has its own granularity dimension and the
// consumer (chart card) typically asks for one metric at a time.

interface SeriesParams extends AnalyticsV2SeriesQueryParams {
  keys: SeriesMetricKey[];
}

const seriesParamsAtom = atom<SeriesParams | null>(null);

const seriesQueryAtom = atomWithQuery((get) => {
  const params = get(seriesParamsAtom);
  const keysCacheKey = params?.keys ? [...params.keys].sort().join(',') : '';
  return {
    queryKey: [
      'analytics-v2',
      'series',
      params?.accountId,
      params?.fromDate,
      params?.toDate,
      params?.granularity,
      keysCacheKey,
      params?.buId ?? 'all',
    ],
    queryFn: () =>
      analyticsV2Service.getSeries(params!.keys, {
        fromDate: params!.fromDate,
        toDate: params!.toDate,
        granularity: params!.granularity,
        accountId: params!.accountId,
        buId: params!.buId,
      }),
    enabled: !!(
      params?.fromDate &&
      params?.toDate &&
      params?.granularity &&
      params?.keys?.length
    ),
    staleTime: 60 * 1000,
  };
});

export function useMetricSeries(
  keys: SeriesMetricKey[],
  fromDate: string,
  toDate: string,
  granularity: Granularity,
  accountId: number,
  buId?: string | null,
): { data: SeriesMetricsBatch | undefined; isLoading: boolean; isError: boolean } {
  const [, setParams] = useAtom(seriesParamsAtom);
  const [query] = useAtom(seriesQueryAtom);

  const keysJoined = useMemo(() => [...keys].sort().join(','), [keys]);

  useEffect(() => {
    if (fromDate && toDate && granularity && keys.length > 0) {
      setParams({ fromDate, toDate, granularity, keys, accountId, buId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, granularity, keysJoined, accountId, buId, setParams]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
